import type { ReactNode } from 'react';
import { styled } from 'styled-components';
import { Link, Ellipsis, Text, WistiaLogo } from '@wistia/vhs';
import { isNotNil } from '~/utilities/type-guards';
import { AccountLogo } from './AccountLogo';

const AccountName = styled(Text)`
  display: flex;
  max-width: 100%;

  &:hover {
    color: ${({ theme }) => theme.color.grey700};
  }
`;

type ContainerProps = {
  $justifyContent: string;
};

const Container = styled.div<ContainerProps>`
  align-items: center;
  display: flex;
  flex-shrink: 3;
  height: 100%;
  justify-content: ${({ $justifyContent }) => $justifyContent};

  /* keeps logo visible */
  margin-right: ${({ $justifyContent }) => ($justifyContent === 'left' ? '3vw' : '0')};
  min-width: 32px;
  overflow: auto;
  position: relative;
`;

const SiteName = styled(Link)`
  align-items: center;
  align-self: stretch;
  color: ${({ theme }) => theme.color.grey900};
  cursor: ${({ href }) => (href ? 'pointer' : 'default')};
  display: inline-flex;
  max-width: 100%;
  overflow: hidden;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.color.grey700};
  }

  &:focus {
    outline: thin dotted ${({ theme }) => theme.color.grey500};
  }
`;

const FallbackLogo = (): ReactNode => (
  <WistiaLogo
    iconOnly={true}
    height={32}
    data-testid="wistia-logo"
  />
);

type Account = {
  __typename?: string;
  id?: string;
  logoUrl?: string | null | undefined;
  displayName?: string | null | undefined;
  name?: string | null | undefined;
};

type NameOrLogoProps = {
  account?: Account | null;
  logoMaxHeight?: number;
};

export const NameOrLogo = ({ account, logoMaxHeight }: NameOrLogoProps): ReactNode => {
  if (isNotNil(account)) {
    const { logoUrl } = account;
    const displayName = account.displayName ?? account.name;

    if (isNotNil(logoUrl)) {
      // a user can have a logo set without necessarily setting an account name
      const altMessage = isNotNil(displayName) ? `${displayName} logo` : 'Account logo';

      return (
        <AccountLogo
          src={logoUrl}
          alt={altMessage}
          data-testid="custom-logo"
          maxHeight={logoMaxHeight ?? undefined}
        />
      );
    }

    if (isNotNil(displayName)) {
      return (
        <AccountName
          variant="headline2"
          data-testid="account-name"
        >
          <Ellipsis>{displayName}</Ellipsis>
        </AccountName>
      );
    }

    return <FallbackLogo />;
  }

  return null;
};

type AccountLogoLinkProps = {
  account?: Account | null;
  href?: string;
  showFallbackLogo?: boolean;
  justifyContent?: string;
  logoMaxHeight?: number;
  children?: ReactNode;
  beforeAction?: () => void;
};

export const AccountLogoLink = ({
  account,
  href = '/home',
  showFallbackLogo,
  justifyContent = 'left',
  logoMaxHeight,
  children = null,
  beforeAction = undefined,
}: AccountLogoLinkProps): ReactNode => {
  return (
    <Container $justifyContent={justifyContent}>
      <SiteName
        href={href}
        beforeAction={beforeAction}
      >
        {showFallbackLogo ? (
          <FallbackLogo />
        ) : (
          <NameOrLogo
            account={account}
            logoMaxHeight={logoMaxHeight}
          />
        )}
      </SiteName>
      {children}
    </Container>
  );
};
